import React from 'react';
import { Typography, Box, Chip, Button, Alert } from '@mui/material';
import http from '../api/http.ts';

// M4 H3 v4 — a HousekeepingTask.Type.CLEANING row's lifecycle now runs exclusively through
// start/finish/inspect (HousekeepingController); PUT .../complete unconditionally 409s
// (TASK_MID_LIFECYCLE) for any CLEANING task that isn't already DONE. This superadmin console has no
// property-scoped lifecycle UI (that lives in the tenant admin app), so a CLEANING task is shown
// read-only here with its real status instead of offering an action guaranteed to fail.
function isBlockedCleaningTask(t: any): boolean {
  return t?.type === 'CLEANING' && t?.status !== 'DONE';
}

// P1-1 (Fable final review 11567500) — the backend now mandates hotelId on PUT .../complete (fails
// closed with a 400 otherwise). This console has no hotel selector, so the ONLY honest source for it is
// the server-resolved `hotelId` GET /tasks already projects per row (from the task's own room) — never
// guessed, never a client-side default. A row with none (unresolvable room) must never attempt the write.
function hasHotelContext(t: any): boolean {
  return t?.hotelId !== null && t?.hotelId !== undefined;
}

const HousekeepingBoard: React.FC = () => {
  const [rows, setRows] = React.useState<any[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    setError(null);
    try {
      const list = await http.get('/api/v1/housekeeping/tasks');
      setRows(Array.isArray(list) ? list : []);
    } catch (err: any) {
      setRows([]);
      setError(err?.response?.data?.message || err?.message || 'Nu am putut încărca sarcinile de housekeeping');
    }
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  // No hotel selector exists in this console, and count-pending now requires a hotelId (no guessed
  // fallback) — derive the pending count from the already-loaded, tenant-scoped rows instead.
  const pendingCount = rows.filter((t: any) => t?.status && t.status !== 'DONE').length;

  const handleComplete = React.useCallback(async (t: any) => {
    setActionError(null);
    if (!hasHotelContext(t)) {
      // Defensive — the button is hidden in this case; never fire a write guaranteed to 400.
      setActionError(`Sarcina #${t.id} nu are o proprietate asociată — acțiunea nu este disponibilă`);
      return;
    }
    try {
      await http.put(`/api/v1/housekeeping/tasks/${t.id}/complete`, undefined, { params: { hotelId: t.hotelId } });
      await reload();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || `Finalizarea sarcinii #${t.id} a eșuat`);
    }
  }, [reload]);

  return (
    <Box p={2}>
      <Typography variant="h6">Housekeeping</Typography>
      <Box mt={1}><Chip label={`Pending: ${pendingCount}`} color={pendingCount > 0 ? 'warning' : 'default'} /></Box>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {actionError && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>}
      <Box mt={2}>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No tasks</Typography>
        ) : (
          <div style={{ border: '1px solid #eee', borderRadius: 8 }}>
            {rows.map((t: any) => {
              const blocked = isBlockedCleaningTask(t);
              return (
                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #f3f3f3' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{t.type || t.status} · Room #{t.roomId}</div>
                    <div style={{ color: '#666', fontSize: 12 }}>
                      Status {t.status || '-'} · Due {t.dueDate || '-'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {blocked ? (
                      <Typography variant="caption" color="text.secondary" title="Curățenia camerelor rulează prin fluxul dedicat Housekeeping din consola de proprietate">
                        Gestionat în consola proprietății
                      </Typography>
                    ) : !hasHotelContext(t) ? (
                      <Typography variant="caption" color="text.secondary" title="Nu s-a putut determina proprietatea camerei pentru această sarcină">
                        Fără proprietate asociată
                      </Typography>
                    ) : (
                      <Button size="small" variant="outlined" disabled={t.status === 'DONE'} onClick={() => handleComplete(t)}>Complete</Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Box>
    </Box>
  );
};

export default HousekeepingBoard;
